// src/components/surveys/builder/SkipPatternEditor.tsx
import React, { useState } from 'react';
import {
  Plus,
  Trash2,
  SkipForward,
  ArrowRight,
} from 'lucide-react';
import {
  SurveyQuestion,
  SkipPattern,
  ConditionOperator,
} from '../../../types/survey.types';

interface SkipPatternEditorProps {
  questions: SurveyQuestion[];
  patterns: SkipPattern[];
  onAddPattern: (pattern: Omit<SkipPattern, 'id'>) => void;
  onUpdatePattern: (patternId: string, updates: Partial<SkipPattern>) => void;
  onDeletePattern: (patternId: string) => void;
}

const OPERATOR_LABELS: Record<ConditionOperator, string> = {
  equals: 'equals',
  not_equals: 'does not equal',
  contains: 'contains',
  not_contains: 'does not contain',
  greater_than: 'is greater than',
  less_than: 'is less than',
  is_empty: 'is empty',
  is_not_empty: 'is not empty',
};

const SkipPatternEditor: React.FC<SkipPatternEditorProps> = ({
  questions,
  patterns,
  onAddPattern,
  onUpdatePattern,
  onDeletePattern,
}) => {
  const [isAdding, setIsAdding] = useState(false);
  const [newPattern, setNewPattern] = useState<{
    questionId: string;
    conditionOperator: ConditionOperator;
    conditionValue: string;
    skipToQuestionId: string;
  }>({
    questionId: '',
    conditionOperator: 'equals',
    conditionValue: '',
    skipToQuestionId: '',
  });

  const handleAddPattern = () => {
    if (!newPattern.questionId || !newPattern.skipToQuestionId) {
      return;
    }

    onAddPattern({
      questionId: newPattern.questionId,
      conditionOperator: newPattern.conditionOperator,
      conditionValue: newPattern.conditionValue,
      skipToQuestionId: newPattern.skipToQuestionId,
    });

    setNewPattern({
      questionId: '',
      conditionOperator: 'equals',
      conditionValue: '',
      skipToQuestionId: '',
    });
    setIsAdding(false);
  };

  const getQuestionById = (id: string) => questions.find((q) => q.id === id);

  const getConditionValueOptions = (questionId: string) => {
    const question = getQuestionById(questionId);
    if (!question) return null;

    if (question.type === 'yes_no') {
      return [
        { value: 'yes', label: 'Yes' },
        { value: 'no', label: 'No' },
      ];
    }

    if (['multiple_choice', 'multi_select', 'dropdown'].includes(question.type)) {
      return question.options?.map((opt) => ({
        value: opt.id,
        label: opt.text,
      }));
    }

    return null;
  };

  const getAvailableSkipTargets = (sourceQuestionId: string) => {
    const sourceQuestion = getQuestionById(sourceQuestionId);
    if (!sourceQuestion) return [];

    // Can only skip to questions that come after the source
    return questions.filter((q) => q.order > sourceQuestion.order);
  };

  return (
    <div className="space-y-4">
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <SkipForward className="h-5 w-5 text-indigo-600" />
          <h3 className="font-medium text-gray-900">Skip Patterns</h3>
        </div>
        <button
          onClick={() => setIsAdding(true)}
          className="flex items-center gap-1 text-sm text-indigo-600 hover:text-indigo-700"
        >
          <Plus className="h-4 w-4" />
          Add Pattern
        </button>
      </div>

      <p className="text-sm text-gray-500">
        Skip patterns allow respondents to jump over questions based on their answers.
      </p>

      {/* Existing patterns */}
      {patterns.length === 0 && !isAdding && (
        <p className="text-sm text-gray-500 text-center py-4 bg-gray-50 rounded-lg">
          No skip patterns configured.
        </p>
      )}

      <div className="space-y-3">
        {patterns.map((pattern) => {
          const sourceQuestion = getQuestionById(pattern.questionId);
          const targetQuestion = getQuestionById(pattern.skipToQuestionId);
          const conditionOptions = getConditionValueOptions(pattern.questionId);
          const conditionLabel = conditionOptions?.find(
            (opt) => opt.value === pattern.conditionValue
          )?.label;

          return (
            <div
              key={pattern.id}
              className="p-4 bg-orange-50 border border-orange-200 rounded-lg"
            >
              <div className="flex items-start justify-between">
                <div className="flex-1">
                  <p className="text-sm text-gray-600">
                    <span className="font-medium">When</span>{' '}
                    <span className="text-indigo-600">
                      Q{(sourceQuestion?.order || 0) + 1}: {sourceQuestion?.text || 'Unknown'}
                    </span>{' '}
                    <span className="font-medium">
                      {OPERATOR_LABELS[pattern.conditionOperator]}
                    </span>{' '}
                    <span className="text-orange-600">
                      "{conditionLabel || pattern.conditionValue}"
                    </span>
                  </p>
                  <p className="text-sm text-gray-600 mt-1 flex items-center gap-1">
                    <ArrowRight className="h-4 w-4" />
                    <span className="font-medium">Skip to</span>{' '}
                    <span className="text-indigo-600">
                      Q{(targetQuestion?.order || 0) + 1}: {targetQuestion?.text || 'Unknown'}
                    </span>
                  </p>
                  <p className="text-xs text-gray-500 mt-2">
                    Skipping {targetQuestion && sourceQuestion
                      ? `${targetQuestion.order - sourceQuestion.order - 1} question(s)`
                      : '?'}
                  </p>
                </div>
                <button
                  onClick={() => onDeletePattern(pattern.id)}
                  className="p-1 text-gray-400 hover:text-red-600 rounded"
                >
                  <Trash2 className="h-4 w-4" />
                </button>
              </div>
            </div>
          );
        })}
      </div>

      {/* Add new pattern form */}
      {isAdding && (
        <div className="p-4 bg-orange-50 border border-orange-200 rounded-lg space-y-4">
          <h4 className="font-medium text-gray-900">New Skip Pattern</h4>

          {/* When condition */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">WHEN</label>
            <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
              {/* Source question */}
              <select
                value={newPattern.questionId}
                onChange={(e) =>
                  setNewPattern({
                    ...newPattern,
                    questionId: e.target.value,
                    conditionValue: '',
                    skipToQuestionId: '',
                  })
                }
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                <option value="">Select question...</option>
                {questions.slice(0, -1).map((q) => (
                  <option key={q.id} value={q.id}>
                    Q{q.order + 1}: {q.text.substring(0, 30)}
                    {q.text.length > 30 ? '...' : ''}
                  </option>
                ))}
              </select>

              {/* Operator */}
              <select
                value={newPattern.conditionOperator}
                onChange={(e) =>
                  setNewPattern({
                    ...newPattern,
                    conditionOperator: e.target.value as ConditionOperator,
                  })
                }
                className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
              >
                {Object.entries(OPERATOR_LABELS).map(([value, label]) => (
                  <option key={value} value={value}>
                    {label}
                  </option>
                ))}
              </select>

              {/* Condition value */}
              {!['is_empty', 'is_not_empty'].includes(newPattern.conditionOperator) && (
                <>
                  {getConditionValueOptions(newPattern.questionId) ? (
                    <select
                      value={newPattern.conditionValue}
                      onChange={(e) =>
                        setNewPattern({ ...newPattern, conditionValue: e.target.value })
                      }
                      className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    >
                      <option value="">Select value...</option>
                      {getConditionValueOptions(newPattern.questionId)?.map((opt) => (
                        <option key={opt.value} value={opt.value}>
                          {opt.label}
                        </option>
                      ))}
                    </select>
                  ) : (
                    <input
                      type="text"
                      value={newPattern.conditionValue}
                      onChange={(e) =>
                        setNewPattern({ ...newPattern, conditionValue: e.target.value })
                      }
                      placeholder="Enter value..."
                      className="p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500"
                    />
                  )}
                </>
              )}
            </div>
          </div>

          {/* Skip to */}
          <div className="space-y-3">
            <label className="block text-sm font-medium text-gray-700">SKIP TO</label>
            <select
              value={newPattern.skipToQuestionId}
              onChange={(e) =>
                setNewPattern({ ...newPattern, skipToQuestionId: e.target.value })
              }
              disabled={!newPattern.questionId}
              className="w-full p-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-orange-500 disabled:bg-gray-100"
            >
              <option value="">Select target question...</option>
              {getAvailableSkipTargets(newPattern.questionId).map((q) => (
                <option key={q.id} value={q.id}>
                  Q{q.order + 1}: {q.text.substring(0, 40)}
                  {q.text.length > 40 ? '...' : ''}
                </option>
              ))}
            </select>
            {newPattern.questionId && getAvailableSkipTargets(newPattern.questionId).length === 0 && (
              <p className="text-sm text-orange-600">
                No questions available to skip to. Questions can only skip forward.
              </p>
            )}
          </div>

          {/* Actions */}
          <div className="flex justify-end gap-3">
            <button
              onClick={() => setIsAdding(false)}
              className="px-4 py-2 text-gray-600 hover:text-gray-800"
            >
              Cancel
            </button>
            <button
              onClick={handleAddPattern}
              disabled={!newPattern.questionId || !newPattern.skipToQuestionId}
              className="px-4 py-2 bg-orange-600 text-white rounded-lg hover:bg-orange-700 disabled:opacity-50 disabled:cursor-not-allowed"
            >
              Add Pattern
            </button>
          </div>
        </div>
      )}
    </div>
  );
};

export default SkipPatternEditor;
